import { ChatMention } from './agents-chat.model';
import { AgentMentionOption } from './agent-mention.model';

// Shared by the composer and the transcript so both agree on what a chip is, and so the
// serialization/segmentation stay unit-testable.

export const MENTION_CHIP_CLASS = 'agent-chip';

// A space doesn't end the query; the overlay closes once nothing matches it.
export function mentionTokenAt(value: string, caret: number): { query: string; start: number } | null {
  const start = value.lastIndexOf('@', caret - 1);
  if (start < 0 || (start > 0 && !/\s/.test(value[start - 1]))) {
    return null;
  }
  const query = value.slice(start + 1, caret);
  return /^\s/.test(query) ? null : { query, start };
}

// Only matched within a single text node, the case while typing.
export interface MentionToken {
  query: string;
  node: Text;
  start: number;
  end: number;
}

export function findMentionToken(root: HTMLElement): MentionToken | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0 || !selection.isCollapsed) {
    return null;
  }
  const node = selection.anchorNode;
  if (!node || node.nodeType !== Node.TEXT_NODE || !root.contains(node)) {
    return null;
  }
  const textNode = node as Text;
  const caret = selection.anchorOffset;
  const token = mentionTokenAt(textNode.textContent ?? '', caret);
  return token ? { query: token.query, node: textNode, start: token.start, end: caret } : null;
}

// contenteditable="false" makes the browser treat the chip as one atomic unit, so a single Backspace
// removes it whole (see adjacentMentionChip).
export function createMentionChip(option: AgentMentionOption): HTMLSpanElement {
  const chip = document.createElement('span');
  chip.className = MENTION_CHIP_CLASS;
  chip.contentEditable = 'false';
  chip.dataset['kind'] = option.kind;
  chip.dataset['id'] = String(option.id);
  chip.dataset['label'] = option.label;
  chip.textContent = `@${option.label}`;
  return chip;
}

function isMentionChip(node: Node): node is HTMLElement {
  return node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).classList.contains(MENTION_CHIP_CLASS);
}

// Chips serialize back to "@Label"; mention index is order of appearance, which is what the backend
// keys on.
export function serializeEditor(root: HTMLElement): { text: string; mentions: ChatMention[] } {
  const mentions: ChatMention[] = [];
  let text = '';
  const walk = (parent: Node): void => {
    parent.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        text += child.textContent ?? '';
        return;
      }
      if (isMentionChip(child)) {
        const label = child.dataset['label'] ?? (child.textContent ?? '').replace(/^@/, '');
        text += `@${label}`;
        mentions.push({
          kind: child.dataset['kind'] ?? '',
          id: child.dataset['id'] ?? '',
          label,
          index: mentions.length,
        });
        return;
      }
      if (child.nodeType === Node.ELEMENT_NODE) {
        const element = child as HTMLElement;
        if (element.tagName === 'BR') {
          text += '\n';
          return;
        }
        // Block wrappers the browser can synthesise on Enter/paste (DIV/P) start a new line.
        if ((element.tagName === 'DIV' || element.tagName === 'P') && text.length > 0 && !text.endsWith('\n')) {
          text += '\n';
        }
        walk(element);
      }
    });
  };
  walk(root);
  return { text, mentions };
}

// Null when no chip is adjacent, so the caller falls back to normal character deletion.
export function adjacentMentionChip(
  root: HTMLElement,
  range: Range,
  direction: 'backward' | 'forward',
): HTMLElement | null {
  const { startContainer, startOffset } = range;
  let candidate: Node | null;
  if (startContainer.nodeType === Node.TEXT_NODE) {
    const textNode = startContainer as Text;
    if (direction === 'backward') {
      if (startOffset > 0) {
        return null;
      }
      candidate = textNode.previousSibling;
    } else {
      if (startOffset < (textNode.textContent?.length ?? 0)) {
        return null;
      }
      candidate = textNode.nextSibling;
    }
  } else {
    const children = startContainer.childNodes;
    candidate = direction === 'backward' ? (children[startOffset - 1] ?? null) : (children[startOffset] ?? null);
  }
  return candidate && isMentionChip(candidate) && root.contains(candidate) ? (candidate as HTMLElement) : null;
}

export interface MessageSegment {
  text: string;
  mention?: { kind: string; id: string; label: string };
}

// Matches labels longest-first, so "@Summer Sale" wins over a bare "@Summer".
export function segmentMentions(text: string, mentions: readonly ChatMention[] | undefined): MessageSegment[] {
  if (!mentions?.length) {
    return [{ text }];
  }
  const labels = [...new Set(mentions.map((mention) => mention.label))]
    .filter((label) => label.length > 0)
    .sort((a, b) => b.length - a.length);
  const byLabel = new Map<string, ChatMention>();
  for (const mention of mentions) {
    if (!byLabel.has(mention.label)) {
      byLabel.set(mention.label, mention);
    }
  }
  const segments: MessageSegment[] = [];
  let buffer = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] === '@') {
      const rest = text.slice(i + 1);
      const label = labels.find((candidate) => rest.startsWith(candidate));
      if (label) {
        if (buffer.length > 0) {
          segments.push({ text: buffer });
          buffer = '';
        }
        const mention = byLabel.get(label)!;
        segments.push({ text: `@${label}`, mention: { kind: mention.kind, id: mention.id, label } });
        i += 1 + label.length;
        continue;
      }
    }
    buffer += text[i];
    i++;
  }
  if (buffer.length > 0) {
    segments.push({ text: buffer });
  }
  return segments;
}
